import React, { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import bkstApi from "../../api";
import RadioCardGroup from "../../common/component/RadioCardGroup";

const BLACKOUT_ACTION_OPTIONS = [
  {
    value: "add",
    title: "Block Date",
    description: "Make selected items unavailable on this date",
  },
  {
    value: "remove",
    title: "Unblock Date",
    description: "Remove the blackout from selected items",
  },
];

export default function BulkSpecialHoursDialog({ open, selectedIds, onClose, onProductsUpdate }) {
  const [action, setAction] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAction("");
      setDate("");
      setSaving(false);
    }
  }, [open]);

  const handleSave = () => {
    if (!date || !action || !selectedIds?.length) return;

    setSaving(true);

    bkstApi
      .put(`/my/products/special-hours`, { ids: selectedIds, action, date })
      .then((res) => {
        localStorage.removeItem("products");
        onProductsUpdate(
          (res.data?.updates || []).map(({ id, rules }) => ({
            id,
            updates: { rules },
          }))
        );
        onClose();
      })
      .catch((error) => {
        console.error("Error updating blackout dates:", error);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const selectedCount = selectedIds?.length || 0;
  const canSave = Boolean(date && action);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative", pr: 6 }}>
        <Typography variant="h6">Blackout Dates</Typography>
        <Typography variant="body2" color="textSecondary">
          {selectedCount} {selectedCount === 1 ? "item" : "items"} selected
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={saving}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box py={1}>
          <RadioCardGroup
            name="blackoutAction"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            options={BLACKOUT_ACTION_OPTIONS}
            direction="row"
          />
        </Box>
        <Box pt={1} pb={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              disablePast
              disabled={!action}
              format="MMM d, y"
              value={date ? moment(date, "YYYY-MM-DD").toDate() : null}
              onChange={(v) => setDate((v && moment(v).format("YYYY-MM-DD")) || "")}
              slotProps={{
                actionBar: { actions: [] },
                toolbar: { hidden: true },
              }}
            />
          </LocalizationProvider>
        </Box>
        {action === "add" && (
          <Alert severity="info">Items that already have this blackout date will be skipped.</Alert>
        )}
        {action === "remove" && (
          <Alert severity="info">Items that do not have this blackout date will be skipped.</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button fullWidth variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
      {saving && (
        <Backdrop open={true} sx={{ zIndex: 2000, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Dialog>
  );
}
